import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import LotteryCodeField from '../../components/LotteryCodeField';
import BankScopeField from '../../components/BankScopeField';
import ScopeField from '../../components/ScopeField';
import { listProducts } from '../../api/products';
import { createCoverageLimit, listCoverageLimits, updateCoverageLimit } from '../../api/coverageLimits';
import './ExpositionPage.css';

// "Superviseur" est ici une vraie 4eme portee (agrege toutes les banks
// supervisees par ce bank, cf. products/services.py:_coverage_scope_targets)
// — distincte de "Banque", contrairement aux autres pages de l'app.
const scopeParams = (filter, scopeId) => {
  if (filter === 'Groupe') return { group: scopeId };
  if (filter === 'Superviseur') return { supervisor: scopeId };
  if (filter === 'Banque') return { bank: scopeId };
  return {};
};

const ExpositionPage = () => {
  const [filter, setFilter] = useState('Tout');
  const [scopeId, setScopeId] = useState('');
  const [scopeName, setScopeName] = useState('');
  const [lottery, setLottery] = useState('1');
  const [lotteryName, setLotteryName] = useState('NY MIDI');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const scope = scopeParams(filter, scopeId);
    Promise.all([
      listProducts(),
      listCoverageLimits({ tirage: lottery, ...scope }),
    ])
      .then(([products, limits]) => {
        if (cancelled) return;
        const limitByProduct = Object.fromEntries(limits.map((l) => [l.product, l]));
        // La couverture Borlette est gérée par Securite > Pousantaj sou Borlet (RÈG 3),
        // plus par CoverageLimit — on retire donc Borlette de ce tableau.
        setRows(products
          .filter((p) => p.abbr !== 'BOR')
          .map((p) => ({
            id: p.id,
            product: p.name,
            configId: limitByProduct[p.id]?.id ?? null,
            pct: limitByProduct[p.id]?.coverage_limit_pct ?? '',
          })));
      })
      .catch(() => { if (!cancelled) setError('Impossible de charger les limites de couverture.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [lottery, filter, scopeId]);

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const updatePct = (rowId, value) => {
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, pct: value } : r)));
  };

  const persistPct = async (rowId) => {
    const row = rows.find((r) => r.id === rowId);
    if (!row || row.pct === '') return;
    const scope = scopeParams(filter, scopeId);
    try {
      if (row.configId) {
        await updateCoverageLimit(row.configId, { coverage_limit_pct: row.pct });
      } else {
        const created = await createCoverageLimit({
          tirage: Number(lottery), product: row.id, ...scope, coverage_limit_pct: row.pct,
        });
        setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, configId: created.id } : r)));
      }
    } catch (err) {
      setError('Sauvegarde de la limite impossible.');
    }
  };

  return (
    <Layout>
      <p className="exp-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <Link to="/securite" className="bc-link">Securite</Link>
        {' >> '}
        <span className="bc-current">Limite d'Exposition</span>
      </p>

      <div className="exp-card">
        <div className="exp-card-header">
          <span className="exp-card-title">Limite d'Exposition</span>
        </div>
        <div className="exp-card-body">
          <div className="exp-radio-row">
            {['Tout', 'Groupe', 'Superviseur', 'Banque'].map((opt) => (
              <label key={opt} className="exp-radio-label">
                <input
                  type="radio"
                  name="filter"
                  value={opt}
                  checked={filter === opt}
                  onChange={() => { setFilter(opt); setScopeId(''); setScopeName(''); }}
                />
                {opt}
              </label>
            ))}
          </div>

          {filter !== 'Tout' && (
            <div className="exp-row exp-scope-row">
              <label className="exp-label">{filter}</label>
              {filter === 'Banque' ? (
                <BankScopeField
                  id={scopeId}
                  name={scopeName}
                  onResolve={(id, name) => { setScopeId(id); setScopeName(name); }}
                  inputClass="exp-input exp-input-sm"
                  iconClass="exp-icon-btn"
                  nameClass="exp-name"
                  errClass="exp-lookup-err"
                />
              ) : (
                <ScopeField
                  filter={filter}
                  id={scopeId}
                  name={scopeName}
                  onResolve={(id, nm) => { setScopeId(id); setScopeName(nm); }}
                  inputClass="exp-input exp-input-sm"
                  iconClass="exp-icon-btn"
                  nameClass="exp-name"
                  errClass="exp-lookup-err"
                />
              )}
            </div>
          )}

          <form onSubmit={handleSearch} className="exp-form">
            <div className="exp-row">
              <label className="exp-label">Loterie</label>
              <LotteryCodeField
                value={lottery}
                name={lotteryName}
                onChange={(code) => { setLottery(code); setLotteryName(''); }}
                onResolve={(code, nm) => { setLottery(code); setLotteryName(nm); }}
                inputClass="exp-input exp-input-sm"
                iconClass="exp-icon-btn"
                nameClass="exp-name"
                allowAll={false}
              />
            </div>

            <button type="submit" className="exp-search-btn">Chercher</button>
          </form>

          {error && <p className="exp-error">{error}</p>}
        </div>
      </div>

      <div className="exp-card">
        <div className="exp-card-header">
          <span className="exp-card-title">Limite d'Exposition</span>
        </div>
        <div className="exp-card-body exp-table-body">
          {loading ? (
            <p>Chargement...</p>
          ) : (
            <>
            <p className="exp-borlette-note">
              La couverture <strong>Borlette</strong> se configure dans{' '}
              <Link to="/securite/borlette-coverage" className="bc-link">Securite &gt; Pousantaj sou Borlet</Link>.
            </p>
            <table className="exp-table">
              <thead>
                <tr>
                  <th>Produit</th>
                  <th>Limite de couverture (%)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="exp-product-plain">{row.product}</td>
                    <td>
                      <input
                        className="exp-cell-input"
                        value={row.pct}
                        onChange={e => updatePct(row.id, e.target.value)}
                        onBlur={() => persistPct(row.id)}
                        placeholder="ex: 60"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ExpositionPage;
